import { QueryClient, useQueryClient } from '@tanstack/react-query';

import React, { createContext, useMemo } from 'react';

import { ParameterTypes, useApiClient } from '@@common';

import { ContentClient, ServiceConnectionClient } from '@@api';
import { ActivityMetadataModel, FlowClient, FlowMetadataModel, ParameterMetadata } from '../../../../api/api';
import { ExpressionMetadataProvider } from '../../../../components/ExpressionMetadataContext';
import useApiFlowMetadata from '../../Hooks/useApiFlowMetadata';
import { FlowQueries } from '../../Hooks/useFlowQueries';
import { getEntityMetadataModelFromJsonSchema } from '../../Services/EntityMetadataModelConverter';
import { ExpectedObjectTypeResolver } from '../../Services/ExpressionVariableScopeCalculatorService';
import ExpressionVariableScopeCalculatorService from '../../Services/ExpressionVariableScopeCalculatorService';
import FlowClipboardService from '../../Services/FlowClipboardService';
import FlowModelConverterService from '../../Services/FlowModelConverterService';
import { ViewLoader } from '../ViewLoader';

export interface IFlowMetadataContext {
	flowMetadata: FlowMetadataModel;
	activityByName: Map<string, ActivityMetadataModel>;
	expressionScopeCalculator: ExpressionVariableScopeCalculatorService;
	flowModelConverterService: FlowModelConverterService;
	clipboardService: FlowClipboardService;
}

class FlowMetadataContextImpl implements IFlowMetadataContext {
	flowMetadata: FlowMetadataModel;
	activityByName: Map<string, ActivityMetadataModel>;
	expressionScopeCalculator: ExpressionVariableScopeCalculatorService;
	flowModelConverterService: FlowModelConverterService;
	clipboardService: FlowClipboardService;

	constructor(flowMetadata: FlowMetadataModel, flowQueries: FlowQueries, queryClient: any, contentClient: ContentClient) {
		this.flowMetadata = flowMetadata;

		this.activityByName = (flowMetadata.activities || []).reduce((acc, activity) => {
			acc.set(activity.name, activity);
			return acc;
		}, new Map<string, ActivityMetadataModel>());

		this.expressionScopeCalculator = new ExpressionVariableScopeCalculatorService(
			flowMetadata.expressions || [],
			flowQueries,
			queryClient,
			createExpectedObjectTypeResolver(contentClient, queryClient),
		);
		this.flowModelConverterService = new FlowModelConverterService(this.activityByName);
		this.clipboardService = new FlowClipboardService(this.activityByName);
	}
}

function getParameterValue(model: Record<string, any>, parameter: ParameterMetadata): any {
	return model[parameter.name] ?? model[parameter.name.charAt(0).toLowerCase() + parameter.name.slice(1)];
}

function createExpectedObjectTypeResolver(contentClient: ContentClient, queryClient: QueryClient): ExpectedObjectTypeResolver {
	return async (_parameterMetadata, referencedParam, model) => {
		if (referencedParam.typeCustom !== ParameterTypes.ContentTemplateId) {
			return undefined;
		}

		const templateId = getParameterValue(model, referencedParam);
		if (!templateId) {
			return undefined;
		}

		const templates = await queryClient.fetchQuery({
			queryKey: ['content', 'templates', 'expression-scope-input-schema'],
			queryFn: () => contentClient.search(0, 100, 'Name', true, undefined, undefined, undefined, undefined),
			staleTime: 10 * 60 * 1000,
		});
		const template = templates.items?.find((item) => (item.templateId || item.id) === templateId);
		const schema = template?.inputSchemaValue?.value;
		return schema ? getEntityMetadataModelFromJsonSchema(schema) : undefined;
	};
}

const emptyFlowMetadata: IFlowMetadataContext = {
	flowMetadata: {
		activities: [],
		expressions: [],
	},
	activityByName: new Map<string, ActivityMetadataModel>(),
	expressionScopeCalculator: null as any,
	flowModelConverterService: new FlowModelConverterService(),
	clipboardService: new FlowClipboardService(),
};

export const FlowMetadataContext = createContext<IFlowMetadataContext>(emptyFlowMetadata);

export default function FlowMetadataProvider({ children }: React.PropsWithChildren<{}>) {
	const [flowMetadataContext, setFlowMetadataContext] = React.useState<IFlowMetadataContext>(emptyFlowMetadata);

	const { data: flowMetadata, isLoading: flowMetadataLoading } = useApiFlowMetadata();
	const flowClient = useApiClient(FlowClient);
	const serviceConnectionClient = useApiClient(ServiceConnectionClient);
	const contentClient = useApiClient(ContentClient);
	const queryClient = useQueryClient();

	const flowQueries = useMemo(() => new FlowQueries(flowClient, serviceConnectionClient), [flowClient, serviceConnectionClient]);

	const isLoaded = useMemo(() => !flowMetadataLoading && flowMetadata, [flowMetadataLoading, flowMetadata]);

	React.useEffect(() => {
		if (flowMetadata) {
			setFlowMetadataContext(new FlowMetadataContextImpl(flowMetadata, flowQueries, queryClient, contentClient));
		}
	}, [flowMetadata, flowQueries, queryClient, contentClient]);

	return (
		<FlowMetadataContext.Provider value={flowMetadataContext}>
			{!isLoaded && <ViewLoader />}
			{isLoaded && (
				<ExpressionMetadataProvider expressions={flowMetadata?.expressions} expressionScopeCalculator={flowMetadataContext.expressionScopeCalculator}>
					{children}
				</ExpressionMetadataProvider>
			)}
		</FlowMetadataContext.Provider>
	);
}
